/**
 * Máquina de estados do ONBOARDING do ARGINK Bridge (R-UX02-P03, §7/§9/§10/§12).
 *
 * Pura e SEM React — só `(estado, evento) → estado`, testável sem DOM/timers reais.
 * `useBridgeOnboarding.ts` é o único lugar que conhece efeito (chamar
 * `detectSigningProvider`/`listProviderCerts`, agendar o próximo poll com
 * `setTimeout`); `BridgePanel`/`SignPage` só LEEM o `BridgeOnboardingState`
 * publicado aqui — nenhum `useState` de loading espalhado por componente.
 *
 * ## Por que a máquina fala `SigningProviderStatus`, não `BridgeStatus`
 *
 * `lib/signing-provider.ts` é o único lugar que conhece o provider da Qualificada —
 * desde a **D-116**, um só: o ARGINK Bridge. Esta máquina não duplica essa decisão,
 * só reage ao resultado dela. Não existe fallback que possa mascarar Bridge ausente:
 * `bridge_absent` conduz à instalação, e nenhum outro programa desta máquina tem como
 * transformar essa ausência em `READY`.
 *
 * ## As dez fases
 *
 * `CHECKING` (checagem avulsa, sem polling) → `NOT_INSTALLED` (nada neste browser) |
 * `CONNECTING` (transporte alcançado, host não respondeu) | `INCOMPATIBLE` (versão não serve).
 * `NOT_INSTALLED` → (usuário baixa) → `WAITING_FOR_INSTALL` (polling ligado, §9) →
 * `CONNECTING` (mais perto) → `READY`. De `READY` em diante: `DISCOVERING_CERTIFICATES`
 * → `CERTIFICATES_READY` | `NO_COMPATIBLE_CERTIFICATE`. Qualquer fase de espera que
 * não resolve em ~90s, ou uma listagem que falha, vai para `RECOVERABLE_ERROR` — nunca
 * um spinner que não termina (§9, "spinner eterno é bug").
 */
import type { AgentCert } from './agent';
import type { BridgeIncompatibilityReason } from './bridge';
import type { SigningProviderKind, SigningProviderStatus } from './signing-provider';

export type BridgeOnboardingPhase =
  | 'CHECKING'
  | 'NOT_INSTALLED'
  | 'WAITING_FOR_INSTALL'
  | 'CONNECTING'
  | 'INCOMPATIBLE'
  | 'READY'
  | 'DISCOVERING_CERTIFICATES'
  | 'CERTIFICATES_READY'
  | 'NO_COMPATIBLE_CERTIFICATE'
  | 'RECOVERABLE_ERROR';

/** Por onde a assinatura vai correr, uma vez `READY` — carregado até os estados de
 * certificado, para que a tela saiba a quem pedir `listProviderCerts`. Com a D-116 há
 * um provider só (`bridge`); o campo `kind` fica porque é ele que a tela lê para saber
 * que já existe provider, não porque haja escolha. */
export interface ReadyProvider {
  kind: SigningProviderKind;
  version: string | null;
  platform: string | null;
}

export type RecoverableErrorReason = 'poll_timeout' | 'certificates_failed';

export interface BridgeOnboardingErrorInfo {
  reason: RecoverableErrorReason;
  detail: string;
  /** Código de suporte, quando a causa veio de uma chamada real (listagem de
   * certificados) — `null` no timeout de instalação, que não tem chamada associada. */
  traceId: string | null;
}

export interface BridgeOnboardingState {
  phase: BridgeOnboardingPhase;
  provider: ReadyProvider | null;
  incompatibilityReason: BridgeIncompatibilityReason | null;
  certs: AgentCert[] | null;
  /** Tentativas de detecção já feitas na janela de espera ATUAL — alimenta o backoff
   * (`nextPollDelayMs`). Zerado sempre que a janela recomeça ou termina. */
  pollAttempt: number;
  /** epoch ms de quando a janela de espera atual começou; `null` fora dela — é contra
   * este valor que `hasPollTimedOut` mede os ~90s. */
  waitStartedAt: number | null;
  error: BridgeOnboardingErrorInfo | null;
}

export const initialBridgeOnboardingState: BridgeOnboardingState = {
  phase: 'CHECKING',
  provider: null,
  incompatibilityReason: null,
  certs: null,
  pollAttempt: 0,
  waitStartedAt: null,
  error: null,
};

export type BridgeOnboardingEvent =
  | { type: 'PROVIDER_DETECTED'; status: SigningProviderStatus; now: number }
  | { type: 'DOWNLOAD_STARTED'; now: number }
  | { type: 'POLL_TIMED_OUT' }
  | { type: 'CERTIFICATES_REQUESTED' }
  | { type: 'CERTIFICATES_LOADED'; certs: AgentCert[] }
  | { type: 'CERTIFICATES_FAILED'; detail: string; traceId: string | null }
  | { type: 'RETRY_REQUESTED'; now: number };

/** `true` sse esta fase mantém o loop de polling ativo — é a ÚNICA leitura que o hook
 * usa para decidir se agenda o próximo tick (nenhuma outra fase gira sozinha). */
export function isPollingPhase(phase: BridgeOnboardingPhase): boolean {
  return phase === 'WAITING_FOR_INSTALL' || phase === 'CONNECTING';
}

/** `true` sse já sabemos por qual provider a assinatura vai correr — vale para
 * `READY` e para todas as fases de certificado que vêm depois dele. */
export function isProviderReady(state: BridgeOnboardingState): boolean {
  return state.provider !== null;
}

// ---------------------------------------------------------------------------
// Temporização — pura, sem `setTimeout`; quem agenda é o hook.
// ---------------------------------------------------------------------------

/** Janela sem detecção depois da qual avisamos que algo exige atenção. ~90s é
 * generoso o bastante para um instalador Windows rodar (download + UAC + registro
 * do host nativo) sem deixar a pessoa esperando indefinidamente sem explicação —
 * e curto o bastante para não parecer que a página travou. */
export const POLL_TIMEOUT_WINDOW_MS = 90_000;

/** Backoff do loop de detecção: rápido no começo (a instalação pode terminar em
 * segundos) e mais econômico depois, com teto de 5s. */
const POLL_BACKOFF_SEQUENCE_MS = [1_000, 2_000, 4_000] as const;
const POLL_BACKOFF_CEILING_MS = 5_000;

export function nextPollDelayMs(pollAttempt: number): number {
  return POLL_BACKOFF_SEQUENCE_MS[pollAttempt] ?? POLL_BACKOFF_CEILING_MS;
}

/** Ritmo do polling "de fundo" depois de `RECOVERABLE_ERROR` (poll_timeout) — mais
 * lento de propósito: a tela já avisou que algo exige atenção; continuar batendo
 * rápido não ajuda. Ainda assim, se a instalação terminar, a tela se recupera
 * sozinha, sem exigir o clique em "Verificar novamente" (§9). */
export const BACKGROUND_POLL_INTERVAL_MS = 15_000;

export function hasPollTimedOut(waitStartedAt: number | null, now: number): boolean {
  return waitStartedAt !== null && now - waitStartedAt >= POLL_TIMEOUT_WINDOW_MS;
}

// ---------------------------------------------------------------------------
// Transições
// ---------------------------------------------------------------------------

function readyStateFrom(status: Extract<SigningProviderStatus, { kind: 'ready' }>): BridgeOnboardingState {
  return {
    phase: 'READY',
    provider: { kind: 'bridge', version: status.version, platform: status.platform },
    incompatibilityReason: null,
    certs: null,
    pollAttempt: 0,
    waitStartedAt: null,
    error: null,
  };
}

/**
 * Traduz um resultado de detecção na fase seguinte, sabendo se a fase ATUAL já
 * mantinha uma janela de espera aberta.
 *
 * A distinção importa: uma checagem AVULSA (a pessoa abriu a etapa, ou clicou
 * "Verificar novamente" antes de baixar qualquer coisa) que não encontra nada vai
 * para `NOT_INSTALLED` — a ação certa é instalar. A MESMA ausência, vista DURANTE
 * o polling pós-download, não regride a tela: o instalador pode estar no meio do
 * caminho, então ela continua esperando e o loop segue.
 */
function applyProviderStatus(
  state: BridgeOnboardingState,
  status: SigningProviderStatus,
  now: number,
): BridgeOnboardingState {
  if (status.kind === 'checking') return state;
  if (status.kind === 'ready') return readyStateFrom(status);

  // Já avisamos "algo exige atenção" — uma detecção de fundo que ainda não achou nada NÃO volta a
  // girar a tela em silêncio. Só `ready` (tratado acima) sai daqui.
  //
  // Mas o estado devolvido precisa ser um objeto NOVO, e é por isso que `pollAttempt` avança aqui
  // sem que nada visível mude: devolver o MESMO objeto não dispara render, o efeito que agenda o
  // próximo poll de fundo não roda de novo, e a repescagem de 15 s aconteceria UMA única vez.
  // O sintoma seria cruel de depurar: quem baixasse o instalador, estourasse os 90 s e só então
  // terminasse a instalação ficaria preso na tela de erro para sempre, embora ela prometa
  // continuar observando sozinha. (Achado P1 do Terra, 08/09.) A fase e a mensagem de erro seguem
  // idênticas — quem lê a tela não vê diferença nenhuma; só o agendador vê.
  if (state.phase === 'RECOVERABLE_ERROR') return { ...state, pollAttempt: state.pollAttempt + 1 };

  const wasWaiting = isPollingPhase(state.phase);
  const attempt = wasWaiting ? state.pollAttempt + 1 : 0;
  const waitStartedAt = wasWaiting ? (state.waitStartedAt ?? now) : now;

  if (status.kind === 'bridge_incompatible') {
    return {
      phase: 'INCOMPATIBLE',
      provider: null,
      incompatibilityReason: status.reason,
      certs: null,
      pollAttempt: 0,
      waitStartedAt: null,
      error: null,
    };
  }

  if (status.kind === 'bridge_host_absent') {
    // O transporte respondeu, então há progresso real — CONNECTING é sempre uma fase
    // de espera, mesmo quando chegamos nela sem nunca ter clicado em baixar (ex.:
    // o host estava rodando antes e caiu; reconectar sozinho é a UX certa).
    return {
      phase: 'CONNECTING',
      provider: null,
      incompatibilityReason: null,
      certs: null,
      pollAttempt: attempt,
      waitStartedAt,
      error: null,
    };
  }

  // bridge_absent
  return {
    phase: wasWaiting ? 'WAITING_FOR_INSTALL' : 'NOT_INSTALLED',
    provider: null,
    incompatibilityReason: null,
    certs: null,
    pollAttempt: wasWaiting ? attempt : 0,
    waitStartedAt: wasWaiting ? waitStartedAt : null,
    error: null,
  };
}

/**
 * A ÚNICA função de transição — todo `dispatch` do hook passa por aqui. Nunca abre
 * nem fecha sessão do envio (§12): ela só conhece detecção de provider e listagem
 * de certificados, nada do domínio da cerimônia.
 */
export function reduceBridgeOnboarding(
  state: BridgeOnboardingState,
  event: BridgeOnboardingEvent,
): BridgeOnboardingState {
  switch (event.type) {
    case 'PROVIDER_DETECTED':
      return applyProviderStatus(state, event.status, event.now);

    case 'DOWNLOAD_STARTED':
      return {
        phase: 'WAITING_FOR_INSTALL',
        provider: null,
        incompatibilityReason: null,
        certs: null,
        pollAttempt: 0,
        waitStartedAt: event.now,
        error: null,
      };

    case 'POLL_TIMED_OUT':
      // Evento de um timer agendado por uma fase que já saiu da espera (ex.: a
      // detecção anterior já tinha resolvido `ready` no meio do caminho) — sem
      // efeito. Guarda contra timer tardio virando regressão de estado.
      if (!isPollingPhase(state.phase)) return state;
      return {
        ...state,
        phase: 'RECOVERABLE_ERROR',
        pollAttempt: 0,
        waitStartedAt: null,
        error: {
          reason: 'poll_timeout',
          detail: 'Ainda não conseguimos detectar a instalação.',
          traceId: null,
        },
      };

    case 'CERTIFICATES_REQUESTED':
      if (state.phase !== 'READY') return state;
      return { ...state, phase: 'DISCOVERING_CERTIFICATES' };

    case 'CERTIFICATES_LOADED':
      if (state.phase !== 'DISCOVERING_CERTIFICATES') return state;
      return {
        ...state,
        phase: event.certs.length > 0 ? 'CERTIFICATES_READY' : 'NO_COMPATIBLE_CERTIFICATE',
        certs: event.certs,
      };

    case 'CERTIFICATES_FAILED':
      if (state.phase !== 'DISCOVERING_CERTIFICATES') return state;
      return {
        ...state,
        phase: 'RECOVERABLE_ERROR',
        certs: null,
        error: { reason: 'certificates_failed', detail: event.detail, traceId: event.traceId },
      };

    case 'RETRY_REQUESTED':
      // Timeout de instalação: "Verificar novamente" RETOMA a mesma espera, com
      // janela nova — o instalador pode legitimamente ainda estar em curso, e a
      // pessoa não devia ter que clicar "Baixar" de novo só para continuarmos
      // olhando (§12 — a jornada não recomeça do zero por causa de um retry técnico).
      if (state.phase === 'RECOVERABLE_ERROR' && state.error?.reason === 'poll_timeout') {
        return {
          phase: 'WAITING_FOR_INSTALL',
          provider: null,
          incompatibilityReason: null,
          certs: null,
          pollAttempt: 0,
          waitStartedAt: event.now,
          error: null,
        };
      }
      // Qualquer outra fase: checagem avulsa do zero. Nunca mexe em sessão/consentimento
      // do envio — só redescobre o provider e, se `ready`, relista certificados.
      return { ...initialBridgeOnboardingState, phase: 'CHECKING' };

    default:
      return state;
  }
}
